import { FC, ReactNode, useEffect, useState } from 'react';
import { getOnFarmPaymentTransactionsByAccountId } from '../../../api/onFarmPaymentTransactionApi';
import { Loader } from '../../../components';
import PagedTable from '../../../components/sorted-table/PagedTable';
import { toastMessages } from '../../../constants/errorMessages';
import { LABEL_CONSTANTS, ON_FARM_BILLING_CONSTANTS, PAYMENT_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { useAppSelector } from '../../../redux/hooks';
import { centsToDollars } from '../../../services/account.service';
import { showToast } from '../../../services/toast.service';
import { OnFarmPaymentMethod, OnFarmPaymentStatus } from '../../../types/enums';
import { OnFarmPaymentTransaction } from '../../../types/interfaces/onFarmPaymentTransaction.interfaces';

const paymentHistoryHeaders = [
  { displayName: TABLE_HEADER_CONSTANTS.BILL_DATE, sortValue: 'billedDate' },
  { displayName: TABLE_HEADER_CONSTANTS.AMOUNT },
  { displayName: ON_FARM_BILLING_CONSTANTS.TANKS },
  { displayName: TABLE_HEADER_CONSTANTS.PAYMENT_METHOD },
  { displayName: TABLE_HEADER_CONSTANTS.REFERENCE_ID },
  { displayName: TABLE_HEADER_CONSTANTS.STATUS },
];

const getPaymentStatus = (status?: OnFarmPaymentTransaction['status']): string => {
  if (status === OnFarmPaymentStatus.NOT_ATTEMPTED) {
    return OnFarmPaymentStatus.FAILED;
  }
  return status || '-';
};

const getStatusClassName = (status?: OnFarmPaymentTransaction['status']): string => {
  const displayStatus = getPaymentStatus(status);
  if (displayStatus === OnFarmPaymentStatus.SUCCESSFUL) {
    return 'on-farm-payment-status on-farm-payment-status--success';
  }
  if (displayStatus === OnFarmPaymentStatus.FAILED) {
    return 'on-farm-payment-status on-farm-payment-status--failed';
  }
  if (displayStatus === OnFarmPaymentStatus.PENDING) {
    return 'on-farm-payment-status on-farm-payment-status--pending';
  }
  return 'on-farm-payment-status';
};

const getPaymentMethod = (history: OnFarmPaymentTransaction): string => {
  const lastFourDigits = history.maskedAccountNumber?.slice(-4);
  const { method } = history;

  if (method === OnFarmPaymentMethod.RECURRING_CARD) {
    return lastFourDigits ? `${PAYMENT_CONSTANTS.CARD}....${lastFourDigits}` : PAYMENT_CONSTANTS.CARD;
  }
  if (method === OnFarmPaymentMethod.ACH) {
    return lastFourDigits ? `${PAYMENT_CONSTANTS.ACH}....${lastFourDigits}` : PAYMENT_CONSTANTS.ACH;
  }
  if (method === OnFarmPaymentMethod.NOT_AVAILABLE) {
    return LABEL_CONSTANTS.NOT_AVAILABLE;
  }

  return '-';
};

const getTanksBilled = (history: OnFarmPaymentTransaction): string => {
  return history.peakActiveTankCount > 0 ? String(history.peakActiveTankCount) : '-';
};

const PaymentStatusBadge = ({ status }: { status?: OnFarmPaymentTransaction['status'] }) => (
  <span className={getStatusClassName(status)}>{getPaymentStatus(status)}</span>
);

const InfoLabel = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="payment-history-content">
    <label>{label}</label>
    <span>{value}</span>
  </div>
);

const OnFarmPaymentHistory: FC = () => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [paymentHistory, setPaymentHistory] = useState<OnFarmPaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadPaymentHistory = async () => {
      if (!accountId) {
        setPaymentHistory([]);
        return;
      }

      try {
        setIsLoading(true);
        const { data } = await getOnFarmPaymentTransactionsByAccountId(accountId, { sort: '^billedDate' });
        setPaymentHistory(Array.isArray(data) ? data : []);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setPaymentHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadPaymentHistory();
  }, [accountId]);

  return (
    <div className="payment-history-container">
      <div className="monthly-on-farm-payment-history">
        <h3>{ON_FARM_BILLING_CONSTANTS.PAYMENT_HISTORY_TITLE}</h3>
        <div className="desk-payment-history">
          {accountId ? (
            <PagedTable
              headers={paymentHistoryHeaders}
              sortBy="^billedDate"
              getData={params => getOnFarmPaymentTransactionsByAccountId(accountId, params)}
              buildRow={history => [
                history?.billedDate ? new Date(history.billedDate).toLocaleDateString('en-US') : '-',
                `$${centsToDollars(history?.costInCents)}`,
                getTanksBilled(history),
                getPaymentMethod(history),
                history?.transactionReferenceId ?? '-',
                <PaymentStatusBadge key="status" status={history?.status} />,
              ]}
              height={240}
              pageSize={6}
              noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            />
          ) : (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          )}
        </div>
        <div className="xs-payment-history">
          {!isLoading && paymentHistory.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            paymentHistory.map(history => (
              <div key={history.onFarmPaymentTransactionId} className="card payment-history-card">
                <div className="payment-history-info">
                  <InfoLabel
                    label={TABLE_HEADER_CONSTANTS.BILL_DATE}
                    value={history.billedDate ? new Date(history.billedDate).toLocaleDateString('en-US') : '-'}
                  />
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.AMOUNT} value={`$${centsToDollars(history.costInCents)}`} />
                  <InfoLabel label={ON_FARM_BILLING_CONSTANTS.TANKS} value={getTanksBilled(history)} />
                </div>
                <div className="payment-history-info">
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.PAYMENT_METHOD} value={getPaymentMethod(history)} />
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.REFERENCE_ID} value={history.transactionReferenceId ?? '-'} />
                  <InfoLabel label={TABLE_HEADER_CONSTANTS.STATUS} value={<PaymentStatusBadge status={history.status} />} />
                </div>
              </div>
            ))
          )}
          {isLoading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>
    </div>
  );
};

export default OnFarmPaymentHistory;
